import React, { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { TOOLS } from "@/constants/testIds";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const fmtMXN = (n) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits: 0,
  }).format(n);

const ProtectionEstimator = () => {
  const [ingreso, setIngreso] = useState(40000);
  const [dependientes, setDependientes] = useState(2);
  const [deudas, setDeudas] = useState(500000);

  const retiro = 65;
  const anios = (dependientes) => Math.max(5, dependientes * 5);

  const estimate = (income, deps, debt) => {
    const reposicionIngresos = income * 12 * anios(deps);
    const cobertura = reposicionIngresos + debt;
    return {
      sumaAsegurada: cobertura,
      anios: anios(deps),
      breakdown: [
        { name: "Ingresos", valor: reposicionIngresos },
        { name: "Deudas", valor: debt },
        { name: "Gastos finales", valor: 200000 },
        { name: "Total", valor: cobertura + 200000 },
      ],
    };
  };

  const { sumaAsegurada, anios: aniosSug, breakdown } = useMemo(
    () => estimate(ingreso, dependientes, deudas),
    [ingreso, dependientes, deudas]
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
      <div className="lg:col-span-5 space-y-10">
        <div>
          <div className="eyebrow text-razmek-gold">Protección patrimonial</div>
          <h2 className="font-serif text-3xl lg:text-4xl mt-3 text-razmek-dark">
            ¿Cuánto debería cubrir tu <span className="italic text-razmek-blue">seguro de vida</span>?
          </h2>
          <p className="mt-4 font-sans text-sm text-razmek-dark/60 max-w-sm">
            Estimación basada en reposición de ingresos por {aniosSug} años más el
            cierre de pasivos.
          </p>
        </div>

        <div>
          <div className="flex justify-between items-baseline mb-3">
            <Label className="eyebrow text-razmek-gray">Ingreso mensual</Label>
            <span className="font-serif text-2xl text-razmek-blue">
              {fmtMXN(ingreso)}
            </span>
          </div>
          <Slider
            data-testid={TOOLS.proteccionIngresos}
            min={10000}
            max={500000}
            step={5000}
            value={[ingreso]}
            onValueChange={(v) => setIngreso(v[0])}
          />
        </div>

        <div>
          <div className="flex justify-between items-baseline mb-3">
            <Label className="eyebrow text-razmek-gray">Dependientes</Label>
            <span className="font-serif text-2xl text-razmek-blue">
              {dependientes}
            </span>
          </div>
          <Slider
            data-testid={TOOLS.proteccionDependientes}
            min={0}
            max={6}
            step={1}
            value={[dependientes]}
            onValueChange={(v) => setDependientes(v[0])}
          />
        </div>

        <div>
          <div className="flex justify-between items-baseline mb-3">
            <Label className="eyebrow text-razmek-gray">Deudas y pasivos</Label>
            <span className="font-serif text-2xl text-razmek-blue">
              {fmtMXN(deudas)}
            </span>
          </div>
          <Slider
            min={0}
            max={10000000}
            step={50000}
            value={[deudas]}
            onValueChange={(v) => setDeudas(v[0])}
          />
        </div>
      </div>

      <div className="lg:col-span-7">
        <div className="border border-razmek p-6 lg:p-8">
          <div className="eyebrow text-razmek-gray">Suma asegurada sugerida</div>
          <div
            data-testid={TOOLS.proteccionResult}
            className="font-serif text-5xl lg:text-6xl text-razmek-blue mt-3"
          >
            {fmtMXN(sumaAsegurada)}
          </div>
          <div className="eyebrow text-razmek-gold mt-2">
            cobertura de vida
          </div>

          <div className="h-[280px] w-full mt-8" style={{ minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart data={breakdown} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid stroke="rgba(29,46,82,0.08)" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fill: "#96999A", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) =>
                    v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : `${v / 1000}K`
                  }
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  tick={{ fill: "#131F28", fontSize: 12, fontFamily: "Cormorant Garamond" }}
                  axisLine={false}
                  tickLine={false}
                  width={130}
                />
                <Tooltip
                  contentStyle={{
                    background: "#131F28",
                    border: "1px solid #D89737",
                    color: "#fff",
                    fontFamily: "Outfit",
                    fontSize: 12,
                  }}
                  formatter={(v) => fmtMXN(v)}
                />
                <Bar
                  dataKey="valor"
                  fill="#1D2E52"
                  radius={[0, 0, 0, 0]}
                  barSize={28}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProtectionEstimator;
